import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Input } from "../components/UI";
import { toast } from "sonner";
import { MessageSquare, Plus, Trash2, Loader2, AlertTriangle, Send, Phone, CheckCircle2, XCircle, Zap } from "lucide-react";

export default function SmsKeywords() {
  const [status, setStatus] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({ keyword: "", fundraising_campaign_id: "", default_amounts: "10,25,50,100", reply_intro: "" });
  const [test, setTest] = useState({ to: "", body: "TotalReach: sample test message from the console." });
  const [testResult, setTestResult] = useState(null);
  const [simulate, setSimulate] = useState({ from_number: "+15125550000", body: "GIVE 50" });
  const [simulateResult, setSimulateResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [pipeTab, setPipeTab] = useState("outbound"); // outbound | inbound

  const load = async () => {
    try {
      const [s, k, fc] = await Promise.all([
        api.get("/political-os/sms/status"),
        api.get("/political-os/sms/keywords"),
        api.get("/fundraising/campaigns"),
      ]);
      setStatus(s.data);
      setKeywords(k.data.keywords || []);
      setCampaigns(fc.data || []);
    } catch (e) { toast.error(errMsg(e, "Could not load SMS setup")); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.keyword || !form.fundraising_campaign_id) {
      toast.error("Pick a keyword and a fundraising campaign");
      return;
    }
    setSaving(true);
    try {
      await api.post("/political-os/sms/keywords", {
        keyword: form.keyword,
        fundraising_campaign_id: form.fundraising_campaign_id,
        default_amounts: form.default_amounts.split(",").map((x) => parseInt(x.trim())).filter(Boolean),
        reply_intro: form.reply_intro,
      });
      toast.success(`Keyword ${form.keyword.toUpperCase()} saved`);
      setForm({ keyword: "", fundraising_campaign_id: "", default_amounts: "10,25,50,100", reply_intro: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const remove = async (kw) => {
    if (!window.confirm(`Delete keyword ${kw}?`)) return;
    try { await api.delete(`/political-os/sms/keywords/${kw}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const sendTest = async () => {
    if (!test.to || !test.body) return;
    setSending(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/political-os/sms/test", test);
      setTestResult(data);
      if (data.sent) toast.success(`Test SMS queued: ${data.sid}`);
      else toast.error(data.reason || "Not sent");
    } catch (e) {
      const msg = errMsg(e);
      setTestResult({ sent: false, reason: msg });
      toast.error(msg);
    } finally { setSending(false); }
  };
  const runSimulate = async () => {
    if (!simulate.body) return;
    setSimulating(true);
    setSimulateResult(null);
    try {
      const { data } = await api.post("/political-os/sms/simulate-inbound", simulate);
      setSimulateResult(data);
      if (data.ok) toast.success("Inbound simulated — reply resolved");
      else toast.error(data.note || "Simulation flagged an issue");
    } catch (e) {
      const msg = errMsg(e);
      setSimulateResult({ ok: false, note: msg, reply: "" });
      toast.error(msg);
    } finally { setSimulating(false); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="sms-keywords-page">
      <PageHeader eyebrow="Fundraising · Text-to-Give"
        title="SMS Keywords"
        subtitle="Set up short keywords supporters can text your Twilio number to donate — no login, no landing page needed."
      />

      {status && !status.configured && (
        <Card className="border-amber-400/40" data-testid="sms-not-configured">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-white">Twilio is not fully configured yet</div>
              <div className="text-xs text-slate-400 mt-1">Add these to <code>backend/.env</code> and redeploy:</div>
              <ul className="mt-2 text-xs text-amber-200 space-y-1 pl-4 list-disc">
                {(status.missing || []).map((m) => <li key={m}>{m}</li>)}
              </ul>
            </div>
          </div>
        </Card>
      )}

      <Card data-testid="sms-setup-guide">
        <h3 className="font-display text-lg font-bold text-white mb-1 flex items-center gap-2">
          <Phone className="w-4 h-4 text-cyan-300" /> Setup guide
        </h3>
        <p className="text-xs text-slate-500 mb-4">A one-time Twilio setup takes 15 minutes. A2P 10DLC registration takes 1–3 business days but you can start it in parallel.</p>
        <ol className="space-y-3 text-sm text-slate-300 list-decimal pl-5">
          <li>
            <b className="text-white">Get your Account SID.</b> Sign in at <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline">console.twilio.com</a>. On the dashboard, copy the <b>Account SID</b> (starts with <code>AC…</code>). Paste it into <code>backend/.env</code> as <code>TWILIO_ACCOUNT_SID=AC…</code>.
          </li>
          <li>
            <b className="text-white">Get an Auth Token.</b> Same dashboard, reveal and copy the <b>Auth Token</b>. Paste as <code>TWILIO_AUTH_TOKEN=…</code> (or leave blank if you prefer the API Key SID + Secret you already gave us).
          </li>
          <li>
            <b className="text-white">Buy a phone number.</b> Twilio Console → <b>Phone Numbers → Manage → Buy a Number</b>. Pick a US local (long code) with SMS enabled. Copy the number in E.164 (<code>+15125551234</code>) and paste as <code>TWILIO_FROM_NUMBER=+…</code>.
          </li>
          <li>
            <b className="text-white">Point the webhook at us.</b> Open the number in the console → <b>Messaging → A Message Comes In</b> → Webhook → <code className="text-cyan-300">https://totalreach.us/api/public/sms/inbound</code> (POST). Save.
          </li>
          <li>
            <b className="text-white">Redeploy backend.</b> The .env changes take effect on the next deploy — Emergent auto-reboots the backend on push.
          </li>
        </ol>

        <div className="mt-6 p-4 rounded-lg bg-amber-400/5 border border-amber-400/30">
          <div className="text-sm font-semibold text-amber-200 mb-1 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Register A2P 10DLC before launch
          </div>
          <p className="text-xs text-slate-300 leading-relaxed">
            US carriers throttle or block political SMS from unregistered numbers. Do this <b>before</b> you drop the number on any flyer:
          </p>
          <ol className="mt-2 text-xs text-slate-300 list-decimal pl-4 space-y-1.5">
            <li>Twilio Console → <b>Messaging → Regulatory Compliance → A2P 10DLC</b>.</li>
            <li>Register your <b>Brand</b> (the political entity — use the legal committee name, EIN and address from Entity Verification).</li>
            <li>Register a <b>Political / Advocacy campaign</b> use case. Sample message: <em>&quot;Reply GIVE to donate to {`{Candidate}`}. Msg&amp;data rates may apply. STOP to end.&quot;</em></li>
            <li>Attach your Twilio number to the approved campaign. Allow 1–3 business days.</li>
          </ol>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <a href="https://www.twilio.com/docs/messaging/compliance/a2p-10dlc" target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline">Twilio A2P 10DLC docs →</a>
            <a href="https://console.twilio.com/us1/develop/sms/regulatory-compliance" target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline">Open A2P Console →</a>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="sms-keyword-form-card">
          <h3 className="font-display text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Plus className="w-4 h-4 text-cyan-300" /> Add a keyword
          </h3>
          <div className="space-y-3">
            <Input label="Keyword" value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value.toUpperCase() })} placeholder="GIVE" data-testid="sms-keyword-input" />
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Fundraising campaign</label>
              <select value={form.fundraising_campaign_id} onChange={(e) => setForm({ ...form, fundraising_campaign_id: e.target.value })}
                      data-testid="sms-fc-select"
                      className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none">
                <option value="">Select a campaign…</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <Input label="Default amounts (comma-separated)" value={form.default_amounts} onChange={(e) => setForm({ ...form, default_amounts: e.target.value })} data-testid="sms-amounts" />
            <Input label="Reply intro (optional)" value={form.reply_intro} onChange={(e) => setForm({ ...form, reply_intro: e.target.value })} placeholder="Thanks for supporting Jane!" data-testid="sms-reply-intro" />
            <Btn onClick={save} disabled={saving} data-testid="sms-keyword-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Publish keyword
            </Btn>
          </div>
        </Card>

        <Card data-testid="sms-test-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
              <Zap className="w-4 h-4 text-cyan-300" /> Test the pipe
            </h3>
            <div className="flex bg-slate-900/60 rounded-md border border-white/10 text-xs">
              <button
                onClick={() => setPipeTab("outbound")}
                data-testid="sms-pipe-tab-outbound"
                className={`px-3 py-1.5 rounded-md ${pipeTab === "outbound" ? "bg-cyan-400/20 text-cyan-200" : "text-slate-400 hover:text-white"}`}>
                Outbound
              </button>
              <button
                onClick={() => setPipeTab("inbound")}
                data-testid="sms-pipe-tab-inbound"
                className={`px-3 py-1.5 rounded-md ${pipeTab === "inbound" ? "bg-cyan-400/20 text-cyan-200" : "text-slate-400 hover:text-white"}`}>
                Inbound (dry-run)
              </button>
            </div>
          </div>

          {pipeTab === "outbound" ? (
            <>
              <p className="text-xs text-slate-500 mb-4">Send a real SMS from your Twilio number to any phone. Detailed Twilio result appears below.</p>
              <div className="space-y-3">
                <Input label="Recipient (E.164)" value={test.to} onChange={(e) => setTest({ ...test, to: e.target.value })} placeholder="+15125551234" data-testid="sms-test-to" />
                <Input label="Message" value={test.body} onChange={(e) => setTest({ ...test, body: e.target.value })} data-testid="sms-test-body" />
                <Btn onClick={sendTest} disabled={sending || !status?.configured} data-testid="sms-test-send-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test
                </Btn>
                {testResult && (
                  <div className={`mt-3 p-3 rounded-md text-xs border ${testResult.sent ? "border-emerald-400/40 bg-emerald-400/5" : "border-rose-400/40 bg-rose-400/5"}`}
                       data-testid="sms-test-result">
                    <div className={`flex items-center gap-2 font-semibold ${testResult.sent ? "text-emerald-200" : "text-rose-200"}`}>
                      {testResult.sent ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      {testResult.sent ? "Delivered to Twilio" : "Twilio rejected the send"}
                    </div>
                    <div className="mt-2 space-y-1 text-slate-300">
                      {testResult.sid && <div><span className="text-slate-500">Message SID:</span> <code className="text-cyan-300">{testResult.sid}</code></div>}
                      {testResult.from && <div><span className="text-slate-500">From:</span> <code>{testResult.from}</code></div>}
                      {testResult.to && <div><span className="text-slate-500">To:</span> <code>{testResult.to}</code></div>}
                      {testResult.reason && <div className="text-rose-200"><span className="text-slate-500">Reason:</span> {testResult.reason}</div>}
                      {(testResult.missing || []).length > 0 && (
                        <div className="text-amber-200"><span className="text-slate-500">Missing env:</span> {(testResult.missing || []).join(", ")}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-4">Dry-run an incoming Twilio webhook. No SMS is sent — you just see the reply body and checkout link your Twilio number would return for that keyword+amount.</p>
              <div className="space-y-3">
                <Input label="Simulated From (E.164)" value={simulate.from_number} onChange={(e) => setSimulate({ ...simulate, from_number: e.target.value })} placeholder="+15125550000" data-testid="sms-sim-from" />
                <Input label="Message body (e.g. 'GIVE 50')" value={simulate.body} onChange={(e) => setSimulate({ ...simulate, body: e.target.value })} data-testid="sms-sim-body" />
                <Btn onClick={runSimulate} disabled={simulating} data-testid="sms-sim-run-btn">
                  {simulating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Simulate inbound
                </Btn>
                {simulateResult && (
                  <div className={`mt-3 p-3 rounded-md text-xs border ${simulateResult.ok ? "border-emerald-400/40 bg-emerald-400/5" : "border-amber-400/40 bg-amber-400/5"}`}
                       data-testid="sms-sim-result">
                    <div className={`flex items-center gap-2 font-semibold ${simulateResult.ok ? "text-emerald-200" : "text-amber-200"}`}>
                      {simulateResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                      {simulateResult.ok
                        ? `Resolved · ${simulateResult.keyword}${simulateResult.amount ? ` $${simulateResult.amount}` : ""}${simulateResult.entity_name ? ` → ${simulateResult.entity_name}` : ""}`
                        : `Blocked at: ${simulateResult.step || "unknown"}`}
                    </div>
                    {simulateResult.reply && (
                      <div className="mt-2 p-2 rounded bg-slate-950/50 border border-white/5">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Reply body</div>
                        <div className="text-slate-200 whitespace-pre-wrap" data-testid="sms-sim-reply-body">{simulateResult.reply}</div>
                      </div>
                    )}
                    {simulateResult.checkout_url && (
                      <div className="mt-2 text-slate-400">
                        <span className="text-slate-500">Checkout URL:</span>{" "}
                        <a href={simulateResult.checkout_url} target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline break-all" data-testid="sms-sim-checkout-url">{simulateResult.checkout_url}</a>
                      </div>
                    )}
                    {simulateResult.note && <div className="mt-2 text-slate-500 italic">{simulateResult.note}</div>}
                  </div>
                )}
              </div>
            </>
          )}
        </Card>
      </div>

      <Card padded={false} data-testid="sms-keywords-table-card">
        <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-cyan-300" />
          <h3 className="font-display text-lg font-bold text-white">Active keywords</h3>
        </div>
        {keywords.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No keywords yet — add one above.</div>
        ) : (
          <table className="w-full text-sm" data-testid="sms-keywords-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                <th className="px-5 py-3">Keyword</th>
                <th className="px-5 py-3">Campaign</th>
                <th className="px-5 py-3">Default amounts</th>
                <th className="px-5 py-3">Created</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keywords.map((k) => {
                const camp = campaigns.find((c) => c.id === k.fundraising_campaign_id);
                return (
                  <tr key={k.id} className="border-b border-white/5" data-testid={`sms-kw-${k.keyword}`}>
                    <td className="px-5 py-3 font-mono text-cyan-300 font-bold">{k.keyword}</td>
                    <td className="px-5 py-3 text-slate-200">{camp?.name || <span className="text-slate-500">deleted?</span>}</td>
                    <td className="px-5 py-3 text-xs text-slate-400">${(k.default_amounts || []).join(", $")}</td>
                    <td className="px-5 py-3 text-xs text-slate-500">{(k.created_at || "").slice(0, 10)}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => remove(k.keyword)} data-testid={`sms-kw-delete-${k.keyword}`} className="text-xs text-rose-300 hover:underline inline-flex items-center gap-1">
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <Card>
        <div className="flex items-start gap-3">
          <Phone className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" />
          <div className="text-xs text-slate-400 leading-relaxed">
            <div className="text-white font-semibold text-sm mb-1">How it works</div>
            Point your Twilio number&apos;s Inbound Message webhook to <code className="text-cyan-300">POST /api/public/sms/inbound</code>. When a supporter texts <code>GIVE 50</code>, we reply with a Stripe checkout link scoped to that campaign, prefilled with $50. Amount-only replies (<code>25</code>) work as follow-ups. All messages are logged for compliance.
          </div>
        </div>
      </Card>
    </div>
  );
}
